import type { CameraView, HudModel, StandingEntry, Viewport } from '../core/types';
import type { Kart } from '../game/kart';
import type { World } from '../game/world';
import type { TrackLayer } from './trackRenderer';
import { drawTrackLayer, drawTrackDynamic, minimapPoint } from './trackRenderer';
import { drawItemBoxes, drawHazards, drawProjectiles } from './itemRenderer';
import { drawKart } from './kartRenderer';
import { drawHud } from './hud';
import { Fx } from './fx';
import { ITEM_BY_ID } from '../data/items';
import { clamp } from '../core/math';
import { TEAM_COLORS, TEAM_NAMES } from '../game/world';

export class GameRenderer {
  private readonly ctx: CanvasRenderingContext2D;
  private dpr = 1;
  readonly fx = new Fx();
  /** éjszakai módhoz: a lámpák világkoordinátái */
  private readonly lampLights: { x: number; y: number }[] = [];
  /** előre renderelt lámpafény-réteg (a teljes pályára) */
  private readonly lampLayer: HTMLCanvasElement | null = null;
  private readonly trackBounds: { minX: number; minY: number; maxX: number; maxY: number };

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly world: World,
    private readonly layer: TrackLayer,
    private readonly minimap: HTMLCanvasElement,
  ) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('A Canvas 2D nem elérhető ebben a böngészőben.');
    this.ctx = ctx;
    this.trackBounds = world.track.bounds;
    for (const prop of world.track.def.props) {
      if (prop.kind !== 'lamp') continue;
      const p = world.track.posAt(prop.s, prop.lateral);
      this.lampLights.push({ x: p.x, y: p.y });
    }
    this.lampLayer = this.buildLampLayer();
    this.resize();
  }

  /** A statikus lámpafényeket egyszer rendereljük egy pálya méretű vászonra. */
  private buildLampLayer(): HTMLCanvasElement | null {
    if (typeof document === 'undefined') return null;
    const b = this.trackBounds;
    const scale = 0.5;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil((b.maxX - b.minX) * scale));
    canvas.height = Math.max(1, Math.ceil((b.maxY - b.minY) * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.globalCompositeOperation = 'lighter';
    for (const lamp of this.lampLights) {
      const x = (lamp.x - b.minX) * scale;
      const y = (lamp.y - b.minY) * scale;
      const r = 145 * scale;
      const glow = ctx.createRadialGradient(x, y, 2, x, y, r);
      glow.addColorStop(0, 'rgba(255, 226, 160, 0.5)');
      glow.addColorStop(0.4, 'rgba(255, 205, 130, 0.17)');
      glow.addColorStop(1, 'rgba(255, 205, 130, 0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    return canvas;
  }

  resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.floor(w * this.dpr);
    this.canvas.height = Math.floor(h * this.dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
  }

  render(alpha = 0): void {
    const ctx = this.ctx;
    const w = window.innerWidth;
    const h = window.innerHeight;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.fillStyle = '#0b0e15';
    ctx.fillRect(0, 0, w, h);

    const views = this.layout(w, h);
    const players = this.world.players;
    for (let i = 0; i < views.length; i++) {
      const kart = players[i];
      if (!kart) continue;
      const vp = views[i];
      const cam = this.camera(kart, vp);
      this.renderWorld(cam, alpha, kart);
      drawHud(ctx, cam, this.hudModel(kart), this.world.time);
      this.drawWarning(ctx, vp, kart);
    }

    if (views.length > 1) {
      ctx.fillStyle = 'rgba(8,10,16,0.92)';
      if (views.length === 2) {
        ctx.fillRect(0, h / 2 - 2, w, 4);
      } else {
        ctx.fillRect(w / 2 - 2, 0, 4, h);
        ctx.fillRect(0, h / 2 - 2, w, 4);
      }
    }
  }

  private layout(w: number, h: number): Viewport[] {    const n = this.world.players.length;
    if (n <= 1) return [{ x: 0, y: 0, width: w, height: h }];
    if (n === 2) {
      return [
        { x: 0, y: 0, width: w, height: h / 2 },
        { x: 0, y: h / 2, width: w, height: h / 2 },
      ];
    }
    return [
      { x: 0, y: 0, width: w / 2, height: h / 2 },
      { x: w / 2, y: 0, width: w / 2, height: h / 2 },
      { x: 0, y: h / 2, width: w / 2, height: h / 2 },
      { x: w / 2, y: h / 2, width: w / 2, height: h / 2 },
    ];
  }

  private camera(kart: Kart, vp: Viewport): CameraView {
    const speed = Math.hypot(kart.vel.x, kart.vel.y);
    const lead = 0.26;
    const ahead = 26;
    const cx = kart.pos.x + kart.vel.x * lead + Math.cos(kart.heading) * ahead;
    const cy = kart.pos.y + kart.vel.y * lead + Math.sin(kart.heading) * ahead;
    const count = this.world.players.length;
    // 1 nézet: közel; 2: közepes; 4: kicsit távolabb, hogy látszódjon a mezőny
    const base = count <= 1 ? 1.55 : count === 2 ? 1.0 : 0.86;
    const zoom = base * (1 + Math.min(speed / 1400, 0.05));
    const yScale = 0.9;
    const halfW = vp.width / (2 * zoom);
    const halfH = vp.height / (2 * zoom * yScale);
    return {
      x: vp.x,
      y: vp.y,
      width: vp.width,
      height: vp.height,
      cx,
      cy,
      zoom,
      yScale,
      bounds: { minX: cx - halfW, minY: cy - halfH, maxX: cx + halfW, maxY: cy + halfH },
    };
  }

  private renderWorld(cam: CameraView, _alpha: number, focus: Kart): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.rect(cam.x, cam.y, cam.width, cam.height);
    ctx.clip();

    ctx.fillStyle = this.world.track.def.ambient.wall;
    ctx.fillRect(cam.x, cam.y, cam.width, cam.height);

    ctx.translate(cam.x + cam.width / 2, cam.y + cam.height / 2);
    ctx.scale(cam.zoom, cam.zoom * cam.yScale);
    ctx.translate(-cam.cx, -cam.cy);

    drawTrackLayer(ctx, this.layer, cam);
    drawTrackDynamic(ctx, this.world.track, cam, this.world.time);
    this.fx.drawSkids(ctx);

    drawItemBoxes(ctx, this.world.items.boxes, this.world.time);
    drawHazards(ctx, this.world.items.hazards, this.world.time);
    drawProjectiles(ctx, this.world.items.projectiles, this.world.time);

    const karts = [...this.world.karts].sort((a, b) => a.pos.y - b.pos.y);
    for (const k of karts) drawKart(ctx, k, this.world.time);
    this.fx.drawParticles(ctx);

    if (this.world.config.night) this.drawNight(ctx, cam, focus);

    // enyhe vignette a nézet szélén
    ctx.restore();
  }

  /** Éjszakai megvilágítás: sötét fátyol, lámpafények és a kocsik fényszórói. */
  private drawNight(ctx: CanvasRenderingContext2D, cam: CameraView, focus: Kart): void {
    const halfW = cam.width / (2 * cam.zoom) + 120;
    const halfH = cam.height / (2 * cam.zoom * cam.yScale) + 120;
    const left = cam.cx - halfW;
    const top = cam.cy - halfH;
    const width = halfW * 2;
    const height = halfH * 2;

    ctx.save();
    ctx.fillStyle = 'rgba(4, 7, 18, 0.74)';
    ctx.fillRect(left, top, width, height);

    // additív fények: az alapot sötétítettük, a fény visszahozza a részleteket
    ctx.globalCompositeOperation = 'lighter';

    // utcai lámpák: egyetlen előre renderelt rétegből, csak a nézetre vágva
    const b = this.trackBounds;
    if (this.lampLayer) {
      const layer = this.lampLayer;
      const lscale = layer.width / Math.max(1, b.maxX - b.minX);
      const sx = Math.max(0, (left - b.minX) * lscale);
      const sy = Math.max(0, (top - b.minY) * lscale);
      const sw = Math.min(layer.width - sx, width * lscale);
      const sh = Math.min(layer.height - sy, height * lscale);
      if (sw > 1 && sh > 1) {
        ctx.drawImage(
          layer,
          sx,
          sy,
          sw,
          sh,
          b.minX + sx / lscale,
          b.minY + sy / lscale,
          sw / lscale,
          sh / lscale,
        );
      }
    }

    // kocsik: fényszóró-kúp + közeli derengés
    for (const kart of this.world.karts) {
      if (kart.finished || kart.out || kart.respawnTimer > 0) continue;

      const halo = ctx.createRadialGradient(kart.pos.x, kart.pos.y, 4, kart.pos.x, kart.pos.y, 110);
      halo.addColorStop(0, 'rgba(255, 246, 214, 0.42)');
      halo.addColorStop(0.5, 'rgba(255, 236, 190, 0.14)');
      halo.addColorStop(1, 'rgba(255, 236, 190, 0)');
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(kart.pos.x, kart.pos.y, 110, 0, Math.PI * 2);
      ctx.fill();

      // 4 nézetnél csak a saját kocsi fényszórója kúpja rajzolódik (teljesítmény)
      if (this.world.players.length > 2 && kart !== focus) continue;

      ctx.save();
      ctx.translate(kart.pos.x, kart.pos.y);
      ctx.rotate(kart.heading);
      const cone = ctx.createRadialGradient(8, 0, 8, 8, 0, 300);
      cone.addColorStop(0, 'rgba(255, 246, 214, 0.66)');
      cone.addColorStop(0.32, 'rgba(255, 238, 192, 0.3)');
      cone.addColorStop(1, 'rgba(255, 238, 192, 0)');
      ctx.fillStyle = cone;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, 300, -0.34, 0.34);
      ctx.closePath();
      ctx.fill();

      // fényes fókuszpont a kocsi előtt
      const hot = ctx.createRadialGradient(34, 0, 2, 34, 0, 64);
      hot.addColorStop(0, 'rgba(255, 252, 235, 0.55)');
      hot.addColorStop(1, 'rgba(255, 248, 220, 0)');
      ctx.fillStyle = hot;
      ctx.beginPath();
      ctx.arc(34, 0, 64, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    ctx.restore();
  }

  private hudModel(kart: Kart): HudModel {
    const itemDef = kart.item ? ITEM_BY_ID[kart.item] : null;
    const standings: StandingEntry[] = this.world.standings.map((k, i) => ({
      kartId: k.id,
      name: k.character.short,
      color: k.battleMode ? TEAM_COLORS[k.team] : k.character.colors.body,
      position: i + 1,
      progress: k.totalProgress,
      isPlayer: k.isPlayer,
      finished: k.finished,
      lives: k.battleMode ? k.lives : undefined,
      team: k.battleMode ? k.team : undefined,
    }));

    let message: string | null = null;
    if (this.world.battle) {
      if (this.world.time < 3.2) message = 'CSATA 2v2';
    } else if (this.world.config.practice && this.world.time < 3.2) {
      message = 'GYAKORLÁS · 3 KÖR';
    }
    if (!this.world.battle && this.world.phase === 'racing' && kart.lap === this.world.config.laps) {
      // csak az utolsó kör elején villan fel, nem takarja végig a képernyőt
      const sinceLap = this.world.time - kart.lapStartTime;
      if (kart.progress > 0.005 && sinceLap < 3.2) message = 'UTOLSÓ KÖR';
    }
    if (kart.finished) message = 'CÉL!';
    if (this.world.battle && kart.out) message = 'KIESTÉL';

    const minimapSize = this.minimap.width || 200;
    const minimapDots = this.world.karts
      .filter((k) => !k.finished && !k.out && k.respawnTimer <= 0)
      .map((k) => {
        const p = minimapPoint(this.world.track, k.progress, minimapSize);
        return {
          x: p.x,
          y: p.y,
          color: k.battleMode ? TEAM_COLORS[k.team] : k.character.colors.body,
          isPlayer: k.isPlayer,
        };
      });

    return {
      kartId: kart.id,
      position: this.world.positionOf(kart),
      total: this.world.karts.length,
      lap: Math.min(kart.lap, this.world.config.laps),
      laps: this.world.config.laps,
      speedKmh: Math.round(kart.speedKmh),
      item: itemDef,
      itemRolling: kart.itemRollTimer > 0,
      itemProgress: kart.itemRollTimer > 0 ? 1 - kart.itemRollTimer / Math.max(0.001, kart.itemRollTotal) : 0,
      driftCharge: kart.driftCharge > 0 ? Math.min(1, kart.driftCharge / 1.9) : 0,
      ability: {
        name: kart.character.ability.name,
        ready: kart.abilityCooldown <= 0,
        ratio: 1 - kart.abilityCooldown / Math.max(0.001, kart.character.ability.cooldown),
      },
      shield: kart.shieldTimer,
      boost: kart.boostTimer,
      slow: kart.slowTimer,
      slowFactor: kart.slowFactor,
      surface: kart.surface,
      standings,
      countdown: this.world.hudCountdown(),
      raceTime: this.world.time,
      lapTime: Math.max(0, this.world.time - kart.lapStartTime),
      bestLap: kart.bestLap,
      trackName: this.world.track.def.name,
      minimap: this.minimap,
      practice: this.world.config.practice === true,
      battle: this.world.battle
        ? {
            team: kart.team,
            teamColor: TEAM_COLORS[kart.team],
            teamName: TEAM_NAMES[kart.team],
            lives: kart.lives,
            teamLives: [this.world.battleLives[0], this.world.battleLives[1]],
            timeLeft: this.world.battleTimeLeft,
            out: kart.out,
          }
        : undefined,
      minimapDots,
      message,
    };
  }

  /** A nézet szélén pulzáló jelzés, ha a játékos lassú vagy járdán van. */
  private drawWarning(ctx: CanvasRenderingContext2D, vp: Viewport, kart: Kart): void {
    const slow = kart.slowTimer > 0;
    const off = kart.surface === 'sidewalk' || kart.surface === 'kerb';
    if (!slow && !off) return;
    const rgb = slow ? '142,197,255' : '239,177,74';
    const alpha = 0.16 + Math.sin(this.world.time * 7) * 0.05;
    ctx.save();
    ctx.strokeStyle = `rgba(${rgb},${alpha.toFixed(3)})`;
    ctx.lineWidth = 8;
    ctx.strokeRect(vp.x + 4, vp.y + 4, vp.width - 8, vp.height - 8);
    ctx.restore();
  }
}
